import "./cache-page.css";
import '../../../css/app.css';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MapComponent from './mapcomponent';
import withNavigation from "../higher-order/navigation";
import { connect } from "react-redux";
import { getConfigurationByKeyNames } from "../../server/services/configuration-service";
import SessionKeys from '../../session'
import { setAlert } from '../actions/alert';
import { apiKey } from "./googleApi";
import { APIProvider } from "@vis.gl/react-google-maps";
import { Round } from "../../utils/NumbersUtils";
import { getCachesHistoriqueByIdClient, getIdClientByProfilePav } from "../../server/services/client-service";
import jwtDecode from "jwt-decode";
import Translator from "../translator";

const COORD_MAX_DECIMAL_PLACES = 8;
const MAX_DMS_MINUTES = 59;
const MAX_DMS_SECONDS = 59.999;
const MAX_LAT_DEG = 90;
const MAX_LON_DEG = 180;
const VOTRE_CACHE_TABLE_DECIMAL_PLACES = 8;
const VOTRE_CACHE_DMS_SEC_DECIMALS = 1;

function formatVotreCacheDecimalLine(n) {
  if (!Number.isFinite(n))
    return '';
  return String(Round(n, VOTRE_CACHE_TABLE_DECIMAL_PLACES));
}


function formatSecondsForDmsDisplay(seconds) {
  const s = Round(seconds, VOTRE_CACHE_DMS_SEC_DECIMALS);
  const fixed = s.toFixed(VOTRE_CACHE_DMS_SEC_DECIMALS);
  return fixed.replace(/\.0$/, '');
}


function decimalToDmsWithHemisphere(decimalDegrees, isLatitude, TEXTS) {
  if (!Number.isFinite(decimalDegrees))
    return '';
  const abs = Math.abs(decimalDegrees);
  const deg = Math.floor(abs);
  const minFloat = (abs - deg) * 60;
  const min = Math.floor(minFloat);
  const secRaw = (minFloat - min) * 60;
  const secStr = formatSecondsForDmsDisplay(secRaw);
  const hem = isLatitude
    ? (decimalDegrees >= 0 ? TEXTS.dmsNorth : TEXTS.dmsSouth)
    : (decimalDegrees >= 0 ? TEXTS.dmsEast : TEXTS.dmsWest);
  return `${deg}°${min}'${secStr}"${hem}`;
}


function clampLatitudeDecimal(n) {
  if (!Number.isFinite(n))
    return 0;
  return Math.min(MAX_LAT_DEG, Math.max(-MAX_LAT_DEG, n));
}


function clampLongitudeDecimal(n) {
  if (!Number.isFinite(n))
    return 0;
  return Math.min(MAX_LON_DEG, Math.max(-MAX_LON_DEG, n));
}


function normalizeLatDegreeInt(raw) {
  if (raw === '' || raw === null || raw === undefined)
    return 0;
  const n = parseFloat(String(raw).replace(',', '.'));
  if (!Number.isFinite(n))
    return 0;
  const t = Math.trunc(n);
  return Math.min(MAX_LAT_DEG, Math.max(-MAX_LAT_DEG, t));
}


function normalizeLonDegreeInt(raw) {
  if (raw === '' || raw === null || raw === undefined)
    return 0;
  const n = parseFloat(String(raw).replace(',', '.'));
  if (!Number.isFinite(n))
    return 0;
  const t = Math.trunc(n);
  return Math.min(MAX_LON_DEG, Math.max(-MAX_LON_DEG, t));
}


function limitFractionDecimalString(raw, maxPlaces) {
  if (raw === '' || raw === null || raw === undefined)
    return '';
  const normalized = String(raw).replace(',', '.');
  const dot = normalized.indexOf('.');
  if (dot === -1)
    return normalized;
  const head = normalized.slice(0, dot + 1);
  const tail = normalized.slice(dot + 1).replace(/\./g, '');
  return head + tail.slice(0, maxPlaces);
}


function normalizeDmsMinutes(raw) {
  if (raw === '' || raw === null || raw === undefined)
    return 0;
  const n = parseFloat(String(raw).replace(',', '.'));
  if (!Number.isFinite(n))
    return 0;
  const t = Math.trunc(n);
  return Math.min(MAX_DMS_MINUTES, Math.max(0, t));
}


function roundLatitudeFraction(raw) {
  if (raw === '' || raw === null || raw === undefined)
    return 0;
  const n = parseFloat(String(raw).replace(',', '.'));
  return Number.isFinite(n)
    ? Round(clampLatitudeDecimal(n), COORD_MAX_DECIMAL_PLACES)
    : 0;
}


function roundLongitudeFraction(raw) {
  if (raw === '' || raw === null || raw === undefined)
    return 0;
  const n = parseFloat(String(raw).replace(',', '.'));
  return Number.isFinite(n)
    ? Round(clampLongitudeDecimal(n), COORD_MAX_DECIMAL_PLACES)
    : 0;
}


function normalizeSecondsValue(raw) {
  if (raw === '' || raw === null || raw === undefined)
    return 0;
  const n = parseFloat(String(raw).replace(',', '.'));
  if (!Number.isFinite(n))
    return 0;
  const clamped = Math.min(MAX_DMS_SECONDS, Math.max(0, n));
  return Round(clamped, COORD_MAX_DECIMAL_PLACES);
}


const CachePage = (props) => {
  const language = props.match?.params?.language;
  const TEXTS = useMemo(() => Translator(language, "TXT_CACHE_PAGE"), [language]);

  const [cachesHistorique, setCachesHistorique] = useState([])
  const [selectedCacheId, setSelectedCacheId] = useState('');
  const [idClientConnected, setIdClientConnected] = useState('');

  const selectedCache = useMemo(() => {
    const selected = selectedCacheId
      ? cachesHistorique.find(c => c.idCache === selectedCacheId)
      : null;
    return selected || cachesHistorique[0] || null;
  }, [cachesHistorique, selectedCacheId]);

  const [searchCoordinates, setSearchCoordinates] = useState({
    searchLat: 0,
    searchLatMin: 0,
    searchLatSec: 0,
    searchLatitude: "0",
    searchLon: 0,
    searchLonMin: 0,
    searchLonSec: 0,
    searchLongitude: "0",
  })
  const [coordinates, setCoordinates] = useState({
    minLat: 0,
    maxLat: 0,
    minLng: 0,
    maxLng: 0,
    center: { lat: 0, lng: 0 }
  })
  const mapRef = useRef(null)

  const isLatitudePoleDegrees = Math.abs(Number(searchCoordinates.searchLat)) === MAX_LAT_DEG;

  const isLongitudeAntimeridianDegrees = Math.abs(Number(searchCoordinates.searchLon)) === MAX_LON_DEG;

  useEffect(() => {
    if (Math.abs(Number(searchCoordinates.searchLat)) !== MAX_LAT_DEG)
      return;
    setSearchCoordinates((prev) => {
      const m = prev.searchLatMin;
      const s = prev.searchLatSec;
      const minOk = m === 0 || m === '0';
      const secNum = normalizeSecondsValue(s);
      if (minOk && secNum === 0)
        return prev;
      return { ...prev, searchLatMin: 0, searchLatSec: 0 };
    });
  }, [searchCoordinates.searchLat]);

  useEffect(() => {
    if (Math.abs(Number(searchCoordinates.searchLon)) !== MAX_LON_DEG)
      return;
    setSearchCoordinates((prev) => {
      const m = prev.searchLonMin;
      const s = prev.searchLonSec;
      const minOk = m === 0 || m === '0';
      const secNum = normalizeSecondsValue(s);
      if (minOk && secNum === 0)
        return prev;
      return { ...prev, searchLonMin: 0, searchLonSec: 0 };
    });
  }, [searchCoordinates.searchLon]);

  useEffect(() => {
    if (!props.authToken) return;

    const loadCacheFromConnectedClient = async () => {
      let idClient = null;

      try {
        const decoded = jwtDecode(props.authToken);
        const idProfile = decoded?.idProfile || null;

        if (idProfile) {
          idClient = await getIdClientByProfilePav(idProfile);
        }
      } catch (e) {
        // console.log('Error decode token / load idClient', e);
      }

      if (!idClient) return;


      getCachesHistoriqueByIdClient(idClient).then((res) => {
        setCachesHistorique(Array.isArray(res) ? res : []);
        setIdClientConnected(idClient);
      });
    };

    loadCacheFromConnectedClient();
  }, [props.authToken])

  useEffect(() => {
    if (!cachesHistorique || cachesHistorique.length === 0)
      return;

    const selected = (selectedCacheId
      ? cachesHistorique.find(c => c.idCache === selectedCacheId)
      : null) || cachesHistorique[0];
    if (!selected)
      return;

    const longitudeSignedDec = selected.longDegDecNeg !== undefined
      ? selected.longDegDecNeg
      : selected.longDegDec !== undefined
        ? selected.longDegDec
        : selected.longDeg;

    setSearchCoordinates({
      searchLat: normalizeLatDegreeInt(selected.latDeg ?? 0),
      searchLatMin: normalizeDmsMinutes(selected.latMin ?? 0),
      searchLatSec: normalizeSecondsValue(selected.latSec ?? 0),
      searchLatitude: roundLatitudeFraction(selected.latDegDec ?? 0),
      searchLon: normalizeLonDegreeInt(selected.longDeg ?? 0),
      searchLonMin: normalizeDmsMinutes(selected.longMin ?? 0),

      searchLonSec: normalizeSecondsValue(selected.longSec ?? 0),
      searchLongitude: roundLongitudeFraction(longitudeSignedDec ?? 0),
    });

    const hiddenIdCacheEl = typeof document !== 'undefined' ? document.getElementById('idCache') : null;
    if (hiddenIdCacheEl && selected.idCache)
      hiddenIdCacheEl.value = selected.idCache;
    if (selectedCacheId !== (selected.idCache || ''))
      setSelectedCacheId(selected.idCache || '');
  }, [cachesHistorique, selectedCacheId])

  useEffect(() => {
    getConfigurationByKeyNames(["LATITUDE_MINIMUM_CACHE", "LATITUDE_MAXIMUM_CACHE", "LONGITUDE_MINIMUM_CACHE", "LONGITUDE_MAXIMUM_CACHE", "DISTANCE_MINIMUM_CACHE"]).then((config) => {
      sessionStorage.setItem(SessionKeys.DISTANCE_MINIMUM_CACHE, config["DISTANCE_MINIMUM_CACHE"].replace(",", "."));
      const minLat = parseFloat(config["LATITUDE_MINIMUM_CACHE"].replace(",", "."));
      const maxLat = parseFloat(config["LATITUDE_MAXIMUM_CACHE"].replace(",", "."));
      const minLng = parseFloat(config["LONGITUDE_MINIMUM_CACHE"].replace(",", "."));
      const maxLng = parseFloat(config["LONGITUDE_MAXIMUM_CACHE"].replace(",", "."));

      setCoordinates({
        minLat,
        maxLat,
        minLng,
        maxLng,
        center: {
          lat: (minLat + maxLat || 0) / 2, lng: ((minLng || 0) + maxLng || 0) / 2,
        }
      });
    })
  }, [])

  const recalculateCoordinates = () => {
    const latDegNum = Number(searchCoordinates.searchLat);
    let latitude;
    if (Math.abs(latDegNum) === MAX_LAT_DEG) {
      latitude = latDegNum >= 0 ? MAX_LAT_DEG : -MAX_LAT_DEG;
    } else {
      let degrees = searchCoordinates.searchLat;
      let degreesAbs = Math.abs(Number(degrees));
      let minutes = searchCoordinates.searchLatMin;
      let seconds = searchCoordinates.searchLatSec;
      latitude = degreesAbs + (normalizeDmsMinutes(minutes) / 60) + (normalizeSecondsValue(seconds) / 3600);
      if (Number(degrees) !== degreesAbs) latitude = latitude * -1;
    }

    const lonDegNum = Number(searchCoordinates.searchLon);
    let longitude;
    if (Math.abs(lonDegNum) === MAX_LON_DEG) {
      longitude = lonDegNum >= 0 ? MAX_LON_DEG : -MAX_LON_DEG;
    } else {
      let degrees = searchCoordinates.searchLon;
      let degreesAbs = Math.abs(Number(degrees));
      let minutes = searchCoordinates.searchLonMin;
      let seconds = searchCoordinates.searchLonSec;
      longitude = degreesAbs + (normalizeDmsMinutes(minutes) / 60) + (normalizeSecondsValue(seconds) / 3600);
      if (Number(degrees) !== degreesAbs) longitude = longitude * -1;
    }

    latitude = clampLatitudeDecimal(latitude);
    longitude = clampLongitudeDecimal(longitude);

    const atLatPole = Math.abs(normalizeLatDegreeInt(searchCoordinates.searchLat)) === MAX_LAT_DEG;
    const atLonAntimeridian = Math.abs(normalizeLonDegreeInt(searchCoordinates.searchLon)) === MAX_LON_DEG;

    setSearchCoordinates(prev => ({
      ...prev,
      searchLatitude: Round(latitude, COORD_MAX_DECIMAL_PLACES),
      searchLongitude: Round(longitude, COORD_MAX_DECIMAL_PLACES),
      searchLat: normalizeLatDegreeInt(prev.searchLat),
      searchLon: normalizeLonDegreeInt(prev.searchLon),
      searchLatMin: atLatPole ? 0 : normalizeDmsMinutes(prev.searchLatMin),
      searchLonMin: atLonAntimeridian ? 0 : normalizeDmsMinutes(prev.searchLonMin),
      searchLatSec: atLatPole ? 0 : normalizeSecondsValue(prev.searchLatSec),
      searchLonSec: atLonAntimeridian ? 0 : normalizeSecondsValue(prev.searchLonSec),
    }));
  }

  useEffect(() => {
    if (Number(searchCoordinates.searchLatitude) === 0 || Number(searchCoordinates.searchLongitude) === 0) return;
    if (!mapRef.current || typeof mapRef.current.coordinateChanged !== "function")
      return;

    mapRef.current.coordinateChanged(searchCoordinates.searchLatitude, searchCoordinates.searchLongitude);
  }, [searchCoordinates.searchLatitude, searchCoordinates.searchLongitude]);

  const handleCoordinateChange = useCallback((e, longueurFixed) => {
    const id = e.target.id;
    const raw = e.target.value.replace(',', '.');

    if (id === 'LatSec' || id === 'LonSec') {
      let limited = limitFractionDecimalString(raw, COORD_MAX_DECIMAL_PLACES);
      if (limited !== '' && !limited.endsWith('.')) {
        const n = parseFloat(limited);
        if (Number.isFinite(n)) {
          const c = Math.min(MAX_DMS_SECONDS, Math.max(0, n));
          limited = String(Round(c, COORD_MAX_DECIMAL_PLACES));
        }
      }
      setSearchCoordinates((prev) => {
        if (id === 'LatSec' && Math.abs(Number(prev.searchLat)) === MAX_LAT_DEG)
          return prev;
        if (id === 'LonSec' && Math.abs(Number(prev.searchLon)) === MAX_LON_DEG)
          return prev;
        return { ...prev, [`search${id}`]: limited };
      });
      return;
    }

    if (id === 'Latitude' || id === 'Longitude') {
      let limited = limitFractionDecimalString(raw, COORD_MAX_DECIMAL_PLACES);
      if (limited !== '' && !limited.endsWith('.')) {
        const n = parseFloat(limited);
        if (Number.isFinite(n)) {
          const c = id === 'Latitude'
            ? clampLatitudeDecimal(n)
            : clampLongitudeDecimal(n);
          limited = String(Round(c, COORD_MAX_DECIMAL_PLACES));
        }
      }
      setSearchCoordinates((prev) => ({ ...prev, [`search${id}`]: limited }));
      return;
    }

    if (id === 'Lat') {
      const nextLat = normalizeLatDegreeInt(raw);
      const atPole = Math.abs(nextLat) === MAX_LAT_DEG;
      setSearchCoordinates((prev) => ({
        ...prev,
        searchLat: nextLat,
        ...(atPole ? { searchLatMin: 0, searchLatSec: 0 } : {}),
      }));
      return;
    }

    if (id === 'Lon') {
      const nextLon = normalizeLonDegreeInt(raw);
      const atAntimeridian = Math.abs(nextLon) === MAX_LON_DEG;
      setSearchCoordinates((prev) => ({
        ...prev,
        searchLon: nextLon,
        ...(atAntimeridian ? { searchLonMin: 0, searchLonSec: 0 } : {}),
      }));
      return;
    }

    if (id === 'LatMin' || id === 'LonMin') {
      const n = Number(raw);
      const v = Number.isFinite(n)
        ? Math.min(MAX_DMS_MINUTES, Math.max(0, Math.trunc(n)))
        : 0;
      setSearchCoordinates((prev) => {
        if (id === 'LatMin' && Math.abs(Number(prev.searchLat)) === MAX_LAT_DEG)
          return prev;
        if (id === 'LonMin' && Math.abs(Number(prev.searchLon)) === MAX_LON_DEG)
          return prev;
        return { ...prev, [`search${id}`]: v };
      });
      return;
    }

    const value = Number(raw);
    setSearchCoordinates((prev) => ({ ...prev, [`search${id}`]: value }));
  }, []);

  const newClickSurLaCarte = (lat, lng) => {

    const latN = clampLatitudeDecimal(Number(lat));
    const lngN = clampLongitudeDecimal(Number(lng));

    let latOri;
    let latMin;
    let latSec;

    if (Math.abs(latN) === MAX_LAT_DEG) {
      latOri = latN >= 0 ? MAX_LAT_DEG : -MAX_LAT_DEG;
      latMin = 0;
      latSec = 0;
    } else {
      let latAbs = Math.abs(latN);
      const latDeg = Math.floor(latAbs);
      latOri = Math.trunc(latN);
      const latMinFloat = (latAbs - latDeg) * 60;
      latMin = Math.floor(latMinFloat);
      const latSecFloat = (latMinFloat - latMin) * 60;
      const latSecClamped = Math.min(MAX_DMS_SECONDS, Math.max(0, latSecFloat));
      latSec = Round(latSecClamped, COORD_MAX_DECIMAL_PLACES);
    }

    let lngOri;
    let lngMin;
    let lngSec;

    if (Math.abs(lngN) === MAX_LON_DEG) {
      lngOri = lngN >= 0 ? MAX_LON_DEG : -MAX_LON_DEG;
      lngMin = 0;
      lngSec = 0;
    } else {
      let lngAbs = Math.abs(lngN);
      const lngDeg = Math.floor(lngAbs);
      lngOri = Math.trunc(lngN);
      const lngMinFloat = (lngAbs - lngDeg) * 60;
      lngMin = Math.floor(lngMinFloat);
      const lngSecFloat = (lngMinFloat - lngMin) * 60;
      const lngSecClamped = Math.min(MAX_DMS_SECONDS, Math.max(0, lngSecFloat));
      lngSec = Round(lngSecClamped, COORD_MAX_DECIMAL_PLACES);
    }

    const latDec = Round(latN, COORD_MAX_DECIMAL_PLACES);
    const lngDec = Round(lngN, COORD_MAX_DECIMAL_PLACES);

    setSearchCoordinates(prev => ({
      ...prev,
      searchLat: latOri,
      searchLatMin: latMin,
      searchLatSec: latSec,
      searchLon: lngOri,
      searchLonMin: lngMin,
      searchLonSec: lngSec,
      searchLatitude: latDec,
      searchLongitude: lngDec,
    }));
  };

  return (<div className="cache-page">
    <div className="section ng-hide">
      <div className="col-xs-12 col-sm-12">
      </div>
    </div>

    <div className="reactive-table-container">
      {props.authToken != null ?
        <div className="votrecache">
          <h1 className="secondary-color-1 center">{TEXTS.titleVotreCache}</h1>
          <h4 className="secondary-color-1 bottom-separator"></h4>
          <div className="col-sm-12 col-xs-12">
            <table className="table table-bordered">
              <tbody>
                <tr>
                  <th className="secondary-color-1-darker ng-binding">{TEXTS.colNoCache}</th>
                  <th className="secondary-color-1-darker ng-binding">{TEXTS.colSecteur}</th>
                  <th className="secondary-color-1-darker ng-binding">{TEXTS.colLatitude}</th>
                  <th className="secondary-color-1-darker ng-binding">{TEXTS.colLongitude}</th>
                  <th className="secondary-color-1-darker ng-binding">{TEXTS.colAnnee}</th>
                  <th className="secondary-color-1-darker ng-binding ng-hide">{TEXTS.colRenouveler}</th>
                  <th className="secondary-color-1-darker ng-binding ng-hide">{TEXTS.colDeplacer}</th>
                </tr>
                {cachesHistorique.map((cache) => {
                  const noCache = cache.noCache || (cache.idCache || "").toString().substring(0, 8);
                  const secteur = cache.nomSecteur || "";
                  const latDecNum = cache.latDegDec !== undefined ? Number(cache.latDegDec) : NaN;
                  const lngDecNum = cache.longDegDecNeg !== undefined
                    ? Number(cache.longDegDecNeg)
                    : cache.longDegDec !== undefined
                      ? Number(cache.longDegDec)
                      : NaN;
                  const latitudeDecimalLine = Number.isFinite(latDecNum) ? formatVotreCacheDecimalLine(latDecNum) : '';
                  const latitudeDmsLine = Number.isFinite(latDecNum) ? decimalToDmsWithHemisphere(latDecNum, true, TEXTS) : '';
                  const longitudeDecimalLine = Number.isFinite(lngDecNum) ? formatVotreCacheDecimalLine(lngDecNum) : '';
                  const longitudeDmsLine = Number.isFinite(lngDecNum) ? decimalToDmsWithHemisphere(lngDecNum, false, TEXTS) : '';
                  const annee = cache.annee !== undefined
                    ? cache.annee
                    : cache.dateEmission
                      ? new Date(cache.dateEmission).getFullYear()
                      : "";

                  return (
                    <tr key={cache.idCache || `${noCache}-${annee}`} onClick={() => {
                      const hiddenIdCacheEl = typeof document !== 'undefined' ? document.getElementById('idCache') : null;
                      if (hiddenIdCacheEl && cache.idCache)
                        hiddenIdCacheEl.value = cache.idCache;
                      setSelectedCacheId(cache.idCache || '');

                      const longitudeSignedDec = cache.longDegDecNeg !== undefined
                        ? cache.longDegDecNeg
                        : cache.longDegDec;
                      setSearchCoordinates({
                        searchLat: normalizeLatDegreeInt(cache.latDeg ?? 0),
                        searchLatMin: normalizeDmsMinutes(cache.latMin ?? 0),
                        searchLatSec: normalizeSecondsValue(cache.latSec ?? 0),
                        searchLatitude: roundLatitudeFraction(cache.latDegDec ?? 0),
                        searchLon: normalizeLonDegreeInt(cache.longDeg ?? 0),
                        searchLonMin: normalizeDmsMinutes(cache.longMin ?? 0),
                        searchLonSec: normalizeSecondsValue(cache.longSec ?? 0),
                        searchLongitude: roundLongitudeFraction(longitudeSignedDec ?? 0),
                      });

                      // Ensure the marker is visible immediately when a cache is selected.
                      if (mapRef.current && typeof mapRef.current.coordinateChanged === "function") {
                        mapRef.current.coordinateChanged(
                          cache.latDegDec ?? cache.latDeg ?? 0,
                          longitudeSignedDec ?? cache.longDegDec ?? cache.longDeg ?? 0
                        );
                      }
                    }}>
                      <td>{noCache}</td>
                      <td>{secteur}</td>
                      <td className="cache-votre-cache-coord">
                        {latitudeDecimalLine ? (
                          <>
                            <div className="cache-votre-cache-coord-decimal">{latitudeDecimalLine}</div>
                            <div className="cache-votre-cache-coord-dms">{latitudeDmsLine}</div>
                          </>
                        ) : null}
                      </td>
                      <td className="cache-votre-cache-coord">
                        {longitudeDecimalLine ? (
                          <>
                            <div className="cache-votre-cache-coord-decimal">{longitudeDecimalLine}</div>
                            <div className="cache-votre-cache-coord-dms">{longitudeDmsLine}</div>
                          </>
                        ) : null}
                      </td>
                      <td>{annee}</td>
                      <td className="ng-hide"></td>
                      <td className="ng-hide"></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {cachesHistorique.length === 0 ? (
              <span>{TEXTS.msgRenouvelerForfait}</span>
            ) : null}
          </div>
        </div>
        :
        <h1 id="valider-deplacer-cache-title" className="secondary-color-1 center ng-binding">
          {TEXTS.titleValiderSansAuth}
        </h1>
      }

      <div className="col-sm-12 col-xs-12">
        <h4 className="col-sm-12 col-xs-12 secondary-color-1 bottom-separator ng-binding">{TEXTS.coordsTitre}</h4>
      </div>
      <div className="col-sm-12 col-xs-12">
        <table className="table table-bordered">
          <tbody>
            <tr>
              <th className="secondary-color-1-darker"></th>
              <th className="secondary-color-1-darker ng-binding">{TEXTS.degres}</th>
              <th className="secondary-color-1-darker ng-binding">{TEXTS.minutes}</th>
              <th className="secondary-color-1-darker ng-binding">{TEXTS.secondes}</th>
              <th className="secondary-color-1-darker ng-binding">{TEXTS.degresDecimaux}</th>
            </tr>
            <tr>
              <th className="secondary-color-1-darker">{TEXTS.latitude}</th>
              <td><input id="Lat" value={searchCoordinates.searchLat} onChange={handleCoordinateChange} onBlur={recalculateCoordinates} /></td>
              <td><input id="LatMin" value={searchCoordinates.searchLatMin} onChange={handleCoordinateChange} onBlur={recalculateCoordinates} disabled={isLatitudePoleDegrees} /></td>
              <td><input id="LatSec" type="text" inputMode="decimal" value={searchCoordinates.searchLatSec} onChange={handleCoordinateChange} onBlur={recalculateCoordinates} disabled={isLatitudePoleDegrees} /></td>
              <td><input id="Latitude" type="text" inputMode="decimal" value={searchCoordinates.searchLatitude} onChange={(e) => handleCoordinateChange(e)} onBlur={() => newClickSurLaCarte(searchCoordinates.searchLatitude, searchCoordinates.searchLongitude)} /></td>
            </tr>
            <tr>
              <th className="secondary-color-1-darker">{TEXTS.longitude}</th>
              <td><input id="Lon" value={searchCoordinates.searchLon} onChange={handleCoordinateChange} onBlur={recalculateCoordinates} /></td>
              <td><input id="LonMin" value={searchCoordinates.searchLonMin} onChange={handleCoordinateChange} onBlur={recalculateCoordinates} disabled={isLongitudeAntimeridianDegrees} /></td>
              <td><input id="LonSec" type="text" inputMode="decimal" value={searchCoordinates.searchLonSec} onChange={handleCoordinateChange} onBlur={recalculateCoordinates} disabled={isLongitudeAntimeridianDegrees} /></td>
              <td><input id="Longitude" type="text" inputMode="decimal" value={searchCoordinates.searchLongitude} onChange={(e) => handleCoordinateChange(e)} onBlur={() => newClickSurLaCarte(searchCoordinates.searchLatitude, searchCoordinates.searchLongitude)} /></td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="col-xs-12 col-sm-12 pull-right">
        <div className="row">
          <input type="hidden" id="postId" name="postId" value="34657" />
          <input type="hidden" id="idCache" name="idCache" defaultValue="" />
          <div className="col-sm-12 col-xs-12">
            <button type="button" className="btn btn-positive btn-end-of-section float-right ng-binding" onClick={e => mapRef.current.validateMarker(1)}>
              {TEXTS.btnValider}
            </button>
          </div>
        </div>
      </div>
      <h1 className="secondary-color-1 center ng-binding">{TEXTS.titleCarte}</h1>
      <div className="CachePage">
        <APIProvider apiKey={apiKey}>
          <MapComponent
            ref={mapRef}
            coordinatesZEC={coordinates}
            newClickSurLaCarte={newClickSurLaCarte}
            idCache={selectedCache?.idCache || ''}
            idZec={props.idZec}
            authToken={props.authToken}
            currentCache={selectedCache}
            idClient={idClientConnected}
            markerTitle={TEXTS.markerTitle}
          />
        </APIProvider>
      </div>
    </div>
  </div>);
}

function mapStateToProps(state) {
  return {
    authToken: state.authToken, parametresWeb: state.parametresWeb, configuration: state.configuration, overlay: state.overlay, entrepriseName: state.entreprise.nom,
  };
}

export default withNavigation(connect(mapStateToProps, { setAlert })(CachePage));
